import React, { useState, useEffect } from 'react';

interface AuthenticatedImageProps {
  src?: string;
  alt: string;
  className?: string;
  onError?: () => void;
}

const AuthenticatedImage: React.FC<AuthenticatedImageProps> = ({ 
  src, 
  alt, 
  className = '',
  onError 
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let isMounted = true;

    if (!src) {
      setLoading(false);
      setError(true);
      return;
    }

    const fetchImage = async () => {
      try {
        setLoading(true);
        setError(false);
        
        // Ensure we're using the correct base URL
        let imageUrl = src;
        if (!src.startsWith('http')) {
          // Handle relative URLs by prepending the API base URL
          if (src.startsWith('/api/')) {
            const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
            // Remove /api from the base URL since src already contains it
            const baseUrl = apiBaseUrl.replace(/\/api$/, '');
            imageUrl = `${baseUrl}${src}`;
          } else {
            imageUrl = `${window.location.origin}${src}`;
          }
        }
        
        const response = await fetch(imageUrl, {
          credentials: 'include', // This ensures cookies are sent with the request
          headers: {
            'Accept': 'image/*'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        
        if (isMounted) {
          setImageSrc(objectUrl);
        }
      } catch (err) {
        console.error('Error loading image:', err);
        if (isMounted) {
          setError(true);
          if (onError) {
            onError();
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchImage();

    // Cleanup function to revoke the object URL
    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, onError]);

  if (loading) {
    return (
      <div className={`animate-pulse bg-gray-200 ${className}`}>
        <div className="w-full h-full" />
      </div>
    );
  }

  if (error || !imageSrc) {
    return (
      <div className={`bg-gray-100 flex items-center justify-center ${className}`}>
        <svg
          className="w-12 h-12 text-gray-400"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      className={className}
    />
  );
};

export default AuthenticatedImage;